import { useEffect, useState } from 'react';
import { useResolvedAccessLevel } from '../components/PermissionsContainer';
import repairOrdersService from '../services/repairOrders.service';
import type { OpenPingFeed } from '../types/repairOrders';

/**
 * The single poll behind BOTH the Repair Orders nav badge and the persistent
 * maintenance toasts. One hook, one request: the badge and the toasts consume
 * the same returned feed, they do not each fetch.
 *
 * AUDIENCE GATE — the load-bearing part. The sidebar is mounted on every page,
 * so an ungated poll is one request per minute for every logged-in user in the
 * company, and a red badge for maintenance's queue on everyone else's screen
 * trains the whole company to ignore it. The gate is the SAME component
 * permission that gates the Dismiss button (`repair-order-ping-clear`, granted
 * to GWI360-Maintenance + GWI360-Developers, already seeded in test AND prod),
 * so the audience cannot drift apart from the people who can act on a ping.
 *
 * `defaultHidden` matters here: with no grant the resolved level is 0, which is
 * also what it is while the permission maps are still loading — so the poll
 * simply does not start until the user is known to be in the audience.
 */
export const OPEN_PING_AUDIENCE_COMPONENT_ID = 'repair-order-ping-clear';

/** Fetch on mount, then once a minute. */
export const OPEN_PING_POLL_MS = 60000;

const EMPTY_FEED: OpenPingFeed = { open: 0, pings: [] };

export function useOpenPingFeed(): OpenPingFeed {
  const inAudience = useResolvedAccessLevel(OPEN_PING_AUDIENCE_COMPONENT_ID, true) >= 1;
  const [feed, setFeed] = useState<OpenPingFeed>(EMPTY_FEED);

  useEffect(() => {
    if (!inAudience) return;

    let cancelled = false;
    const controller = new AbortController();

    const poll = async () => {
      try {
        const data = await repairOrdersService.getOpenPingFeed(controller.signal);
        if (cancelled) return;
        setFeed({
          open: typeof data?.open === 'number' ? data.open : 0,
          pings: Array.isArray(data?.pings) ? data.pings : [],
        });
      } catch (err) {
        // DEGRADE SILENTLY. A transient blip must keep the last known count and
        // raise no error toast: this polls forever in the background, and one
        // error toast a minute would be worse than the missed request the
        // feature exists to prevent. Console only.
        if (!cancelled) {
          console.debug('[useOpenPingFeed] open-ping poll failed; keeping last known count', err);
        }
      }
    };

    void poll();
    const timer = setInterval(() => { void poll(); }, OPEN_PING_POLL_MS);

    return () => {
      // `cancelled` guards setState after unmount; abort() kills the in-flight
      // request so a slow response cannot resolve into a dead tree.
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, [inAudience]);

  return feed;
}

export default useOpenPingFeed;
